import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import Popper from '@mui/material/Popper';
import './FieldStatus.scss';

/** Declarative option, Angular `<ng-option [value]="x">label</ng-option>`. */
export interface SelectOptionProps {
  value: unknown;
  disabled?: boolean;
  children?: ReactNode;
}

export function SelectOption(props: SelectOptionProps): ReactElement | null {
  void props;
  return null;
}

export interface SelectProps<T = unknown> {
  /** Angular `[items]`. */
  items?: readonly T[];
  /** Property shown as the label (default 'label'); primitives are shown as they are. */
  bindLabel?: string;
  /** When set, the model is `item[bindValue]`; otherwise it is the item itself. */
  bindValue?: string;
  /** The model (Angular `ngModel`/form control value). */
  value?: unknown;
  /** ngModelChange + (change): the new model value and the selected item (undefined when cleared, model null). */
  onChange?: (value: unknown, item: T | undefined) => void;
  /** Default true, like ng-select. */
  clearable?: boolean;
  /** Default true. */
  searchable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Classes on the select host (Angular `class=`). */
  className?: string;
  /** Classes on the dropdown panel (Angular `panelClass`). */
  panelClass?: string;
  /** The panel is at least this wide in px (it is otherwise as wide as the select), for options longer than the box. */
  panelMinWidth?: number;
  /** Angular `(focus)="select.open()"`: open the list whenever the input receives focus. */
  openOnFocus?: boolean;
  groupBy?: string | ((item: T) => string);
  /** Angular `ng-option-tmp`. */
  renderOption?: (item: T, context: { searchTerm: string }) => ReactNode;
  /** Angular `ng-label-tmp`. */
  renderLabel?: (item: T) => ReactNode;
  notFoundText?: string;
  /**
   * An action offered under `notFoundText` when nothing matches the search (e.g. create what was searched for). It is
   * given the search term and a `close` for the list, so the action can close it before opening anything else.
   */
  notFoundAction?: (context: { searchTerm: string; close: () => void }) => ReactNode;
  clearAllText?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** `<SelectOption>` children (declarative options). */
  children?: ReactNode;
}

interface Option<T> {
  index: number;
  item: T;
  value: unknown;
  label: string;
  labelNode?: ReactNode;
  disabled: boolean;
  group?: string;
}

type Row<T> = { kind: 'group'; key: string; label: string } | { kind: 'option'; option: Option<T> };

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

function read(item: unknown, key: string): unknown {
  return item !== null && typeof item === 'object' ? (item as Record<string, unknown>)[key] : undefined;
}

/**
 * Angular `ng-select` for React: same DOM and class names (so the ported ng-select theme and the legacy overrides
 * apply), same behaviour: click opens (or toggles when not searchable), arrow toggles, typing filters, Enter picks
 * the marked option, Escape/Tab/outside click close, Backspace on an empty search clears, clearing gives null.
 */
export function Select<T = unknown>({
  items,
  bindLabel = 'label',
  bindValue,
  value,
  onChange,
  clearable = true,
  searchable = true,
  placeholder = '',
  disabled = false,
  className = '',
  panelClass = '',
  panelMinWidth,
  openOnFocus = false,
  groupBy,
  renderOption,
  renderLabel,
  notFoundText = 'No items found',
  notFoundAction,
  clearAllText = 'Clear all',
  id,
  name,
  'aria-label': ariaLabel,
  onFocus,
  onBlur,
  children,
}: SelectProps<T>) {
  const reactId = useId();
  const dropdownId = `${reactId}-panel`;
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [markedIndex, setMarkedIndex] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<{ element: HTMLElement; width: number } | null>(null);

  const options = useMemo<Option<T>[]>(() => {
    const list: Option<T>[] = [];
    if (items) {
      items.forEach((item) => {
        const label =
          item !== null && typeof item === 'object' ? String(read(item, bindLabel) ?? '') : String(item ?? '');
        const group =
          groupBy === undefined
            ? undefined
            : String(typeof groupBy === 'function' ? groupBy(item) : (read(item, groupBy) ?? ''));
        list.push({
          index: list.length,
          item,
          value: bindValue ? read(item, bindValue) : item,
          label,
          disabled: Boolean(read(item, 'disabled')),
          group,
        });
      });
    }
    Children.forEach(children, (child) => {
      if (isValidElement<SelectOptionProps>(child) && child.type === SelectOption) {
        list.push({
          index: list.length,
          item: { value: child.props.value, label: textOf(child.props.children) } as unknown as T,
          value: child.props.value,
          label: textOf(child.props.children),
          labelNode: child.props.children,
          disabled: Boolean(child.props.disabled),
        });
      }
    });
    return list;
  }, [items, children, bindLabel, bindValue, groupBy]);

  const selected = useMemo<Option<T> | undefined>(() => {
    if (value === null || value === undefined) return undefined;
    const found = options.find((o) => Object.is(o.value, value));
    if (found) return found;
    // ng-select `_handleWriteValue`: a model that matches no item is still shown. An object (or a primitive without
    // bindValue) is its own item; with bindValue the item is `{ [bindLabel]: null, [bindValue]: value }`, so a label
    // template renders it with empty fields and the plain label is empty. (An object model with bindValue is refused.)
    const isObject = typeof value === 'object';
    if (isObject && bindValue) return undefined;
    if (Array.isArray(value)) return undefined;
    if (isObject || !bindValue) {
      const label = isObject ? String(read(value, bindLabel) ?? '') : String(value);
      return { index: -1, item: value as T, value, label, disabled: false };
    }
    return {
      index: -1,
      item: { [bindLabel]: null, [bindValue]: value } as unknown as T,
      value,
      label: '',
      disabled: false,
    };
  }, [options, value, bindLabel, bindValue]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;
  }, [options, searchTerm]);

  const rows = useMemo<Row<T>[]>(() => {
    if (groupBy === undefined) return filtered.map((option) => ({ kind: 'option', option }));
    const order: string[] = [];
    filtered.forEach((o) => {
      if (!order.includes(o.group ?? '')) order.push(o.group ?? '');
    });
    return order.flatMap((key) => [
      { kind: 'group' as const, key, label: key },
      ...filtered.filter((o) => (o.group ?? '') === key).map((option) => ({ kind: 'option' as const, option })),
    ]);
  }, [filtered, groupBy]);

  const selectable = useMemo(() => filtered.filter((o) => !o.disabled), [filtered]);

  const hasValue = selected !== undefined;
  const showClear = clearable && (hasValue || searchTerm.length > 0) && !disabled;

  const open = () => {
    if (disabled || isOpen) return;
    if (rootRef.current) setAnchor({ element: rootRef.current, width: rootRef.current.offsetWidth });
    setIsOpen(true);
    setMarkedIndex(
      selected && selected.index >= 0 && !selected.disabled ? selected.index : (selectable[0]?.index ?? null),
    );
  };

  const close = () => {
    if (!isOpen) return;
    setIsOpen(false);
    setSearchTerm('');
    setMarkedIndex(null);
  };

  const choose = (option: Option<T>) => {
    if (option.disabled || disabled) return;
    if (!selected || selected.index !== option.index) {
      onChange?.(option.value, option.item);
    }
    close();
  };

  const clear = () => {
    if (hasValue) onChange?.(null, undefined);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const move = (delta: 1 | -1) => {
    if (selectable.length === 0) return;
    const at = selectable.findIndex((o) => o.index === markedIndex);
    const next =
      at === -1 ? (delta === 1 ? 0 : selectable.length - 1) : (at + delta + selectable.length) % selectable.length;
    setMarkedIndex(selectable[next].index);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onDocumentClick = (event: Event) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearchTerm('');
      setMarkedIndex(null);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || markedIndex === null) return;
    panelRef.current?.querySelector('.ng-option-marked')?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, markedIndex]);

  const onContainerMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (disabled) return;
    if (target.tagName !== 'INPUT') event.preventDefault();
    if (target.classList.contains('ng-clear-wrapper') || target.closest('.ng-clear-wrapper')) {
      clear();
      return;
    }
    if (target.classList.contains('ng-arrow-wrapper') || target.classList.contains('ng-arrow')) {
      inputRef.current?.focus();
      if (isOpen) close();
      else open();
      return;
    }
    inputRef.current?.focus();
    if (searchable) open();
    else if (isOpen) close();
    else open();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (isOpen) move(1);
        else open();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) move(-1);
        else open();
        break;
      case 'Enter':
        event.preventDefault();
        if (isOpen) {
          const marked = options.find((o) => o.index === markedIndex);
          if (marked) choose(marked);
        } else {
          open();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
      case ' ':
        if (!searchable && !isOpen) {
          event.preventDefault();
          open();
        }
        break;
      case 'Backspace':
        if (searchTerm === '' && clearable && hasValue) {
          onChange?.(null, undefined);
        }
        break;
    }
  };

  const hostClass = [
    'ng-select',
    'ng-select-single',
    searchable && 'ng-select-searchable',
    clearable && 'ng-select-clearable',
    isOpen && 'ng-select-opened',
    focused && 'ng-select-focused',
    searchTerm && 'ng-select-filtered',
    disabled && 'ng-select-disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={hostClass} ref={rootRef}>
      <div className={`ng-select-container${hasValue ? ' ng-has-value' : ''}`} onMouseDown={onContainerMouseDown}>
        <div className="ng-value-container">
          <div className="ng-placeholder">{placeholder}</div>
          {selected && (
            <div className="ng-value">
              {renderLabel ? (
                renderLabel(selected.item)
              ) : (
                <span className="ng-value-label">{selected.labelNode ?? selected.label}</span>
              )}
            </div>
          )}
          <div
            className="ng-input"
            role="combobox"
            aria-expanded={isOpen}
            aria-owns={isOpen ? dropdownId : undefined}
            aria-haspopup="listbox"
          >
            <input
              ref={inputRef}
              id={id}
              name={name}
              aria-label={ariaLabel}
              readOnly={!searchable}
              disabled={disabled}
              value={searchTerm}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls={isOpen ? dropdownId : undefined}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setMarkedIndex(
                  options.filter(
                    (o) => !o.disabled && o.label.toLowerCase().includes(event.target.value.trim().toLowerCase()),
                  )[0]?.index ?? null,
                );
                if (!isOpen) {
                  if (rootRef.current) setAnchor({ element: rootRef.current, width: rootRef.current.offsetWidth });
                  setIsOpen(true);
                }
              }}
              onKeyDown={onKeyDown}
              onFocus={(event) => {
                setFocused(true);
                onFocus?.(event);
                if (openOnFocus) open();
              }}
              onBlur={(event) => {
                setFocused(false);
                onBlur?.(event);
              }}
            />
          </div>
        </div>
        {showClear && (
          <span className="ng-clear-wrapper" tabIndex={0} title={clearAllText}>
            <span className="ng-clear" aria-hidden="true">
              ×
            </span>
          </span>
        )}
        <span className="ng-arrow-wrapper">
          <span className="ng-arrow"></span>
        </span>
      </div>

      {isOpen && anchor && (
        <Popper
          open
          anchorEl={anchor.element}
          placement="bottom-start"
          style={{ zIndex: 1500, width: Math.max(anchor.width, panelMinWidth ?? 0) }}
        >
          <div
            ref={panelRef}
            id={dropdownId}
            className={`ng-dropdown-panel ng-select-bottom ${panelClass}`.trim()}
            style={{ opacity: 1, position: 'relative' }}
            role="listbox"
            aria-label="Options list"
          >
            <div className="ng-dropdown-panel-items scroll-host">
              {rows.map((row) =>
                row.kind === 'group' ? (
                  <div key={`group-${row.key}`} className="ng-option ng-optgroup" role="group">
                    <span className="ng-option-label">{row.label}</span>
                  </div>
                ) : (
                  <div
                    key={row.option.index}
                    className={[
                      'ng-option',
                      row.option.disabled && 'ng-option-disabled',
                      selected?.index === row.option.index && 'ng-option-selected',
                      groupBy !== undefined && 'ng-option-child',
                      markedIndex === row.option.index && 'ng-option-marked',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="option"
                    aria-selected={selected?.index === row.option.index}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(row.option)}
                    onMouseOver={() => !row.option.disabled && setMarkedIndex(row.option.index)}
                  >
                    {renderOption ? (
                      renderOption(row.option.item, { searchTerm })
                    ) : (
                      <span className="ng-option-label">{row.option.labelNode ?? row.option.label}</span>
                    )}
                  </div>
                ),
              )}
              {filtered.length === 0 && <div className="ng-option ng-option-disabled">{notFoundText}</div>}
              {filtered.length === 0 && notFoundAction && (
                // Keeps focus in the search box, as choosing an option does.
                <div className="ng-option" onMouseDown={(event) => event.preventDefault()}>
                  {notFoundAction({ searchTerm, close })}
                </div>
              )}
            </div>
          </div>
        </Popper>
      )}
    </div>
  );
}
